<template>
  <div>
    <SelectSourceModal
      v-if="showModal('SelectSourceModal')" />
    <FileUploaderModal
      v-if="showModal('FileUploaderModal')" />
    <ChooseExistingFilesModal
      v-if="showModal('ChooseExistingFilesModal')" />
    <ManageStatusAttachmentItemModal
      v-if="showModal('ManageStatusAttachmentItemModal')"
      :attachmentItem="attachment.files[selectedAttachmentItemIndex]"
      :onUpdate="onUpdatedScheduleAttachmentItem" />
    <Section>
      <template v-slot:section-header>
        <h5 class="title">ไฟล์แนบ</h5>
        <div class="action">
          <div class="btn-container">
            <button
              v-if="!disabledEdit"
              type="button"
              class="btn btn-light mt-2"
              @click="setActiveModal('SelectSourceModal')">
              เพิ่มไฟล์แนบ
            </button>
          </div>
        </div>
      </template>
      <div class="section-attachment-container">
        <div
          class="errorDialogue"
          v-if="titleIsEmpty">
          <div class="alert alert-danger">
            <p>มีข้อผิดพลาดเกิดขึ้น</p>
            <ul>
              <li>กรุณาตั้งชื่อหัวข้อไฟล์แนบ</li>
            </ul>
          </div>
        </div>
        <List v-if="!isEmpty()">
          <template v-slot:list-body>
            <div class="attachment-title-container">
              <div class="row">
                <div class="col-md-12">
                  <h5><i class="fas fa-cogs"></i> ตั้งชื่อหัวข้อไฟล์แนบ</h5>
                  <FormGroup>
                    <input
                      :disabled="disabledEdit"
                      type="text"
                      placeholder="ตั้งชื่อหัวข้อไฟล์แนบ"
                      class="form-control"
                      v-model="attachment.name"
                      @change="checkTitleRequired" />
                  </FormGroup>
                </div>
              </div>
            </div>
            <h5 class="name"><i class="fas fa-folder-open"></i> รายการไฟล์แนบ</h5>
            <div class="list-item-container no-underline"
               v-for="(item, itemIndex) in attachment.files"
               :key="itemIndex">
              <div class="list-item-icon">
                <i :class="getFileIconClass(item.type)"></i>
              </div>
              <div class="list-item-detail">
                <div class="list-item list-item-primary list-item-title">
                  <div
                    @click.stop="onFileNameClicked(item)"
                    v-if="!editingFile || editingFile && editingFile.name !== item.name"
                    class="title">
                    {{item.name}}.{{item.type}} <i class="far fa-edit" v-if="!disabledEdit"></i>
                  </div>
                  <div
                    v-if="editingFile && editingFile.name === item.name"
                    class="input-container">
                    <input
                      type="text"
                      v-model="editingName"
                      class="input-title">
                    <i @click.stop="onSaveFileName" class="fas fa-save"></i>
                    <i @click.stop="onCancelEditingFileName" class="fas fa-times"></i>
                  </div>
                  <div class="subtitle">ขนาดไฟล์: {{prettyFileSize(item.fileSize)}}</div>
                </div>
                <div class="list-item list-item-secondary">
                  <div class="dropdown">
                    <select
                      :disabled="disabledEdit"
                      v-model="item.status"
                      @change="changePublishStatus(itemIndex)">
                      <option
                        v-for="(status, statusIndex) in statusOptions"
                        :key="statusIndex"
                        :value="status.key">
                        {{status.name}}
                      </option>
                    </select>
                    <div
                      class="attachment-scheduler"
                      v-if="item.status === 'scheduled'">
                      <div v-if="item.startPublish">
                        <p>
                          <i class="fas fa-play-circle"></i>
                          {{formatDateTime(item.startPublish)}}
                        </p>
                        <p>
                          <i class="fas fa-stop-circle"></i>
                          {{item.endPublish ? formatDateTime(item.endPublish)  : 'ไม่ระบุวันสิ้นสุดการเผยแพร่'}}
                        </p>
                        <p>
                          <a
                            class="a-schedule-attachment"
                            @click="changePublishStatus(itemIndex)">
                            <i class="fas fa-edit"></i> แก้ไขเวลาเผยแพร่
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="list-item list-item-action">
                  <div class="action-container">
                    <a
                      target="_blank"
                      class="btn btn-light btn-action"
                      @click.stop="downloadWithExtension(item, item.baseUrl)">
                      <i class="fas fa-file-download"></i>
                    </a>
                    <DoubleTabToConfirmButton
                      v-if="!disabledEdit"
                      text="คลิกอีกครั้งเพื่อนำไฟล์ออกจากรายการ"
                      iconClass="far fa-trash-alt"
                      @confirm="onUnAttachFile(item.id)"/>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </List>
      </div>
      <!-- empty attachment -->
      <EmptyMessage
        v-if="isEmpty()"
        class="empty-small">
        <h5 class="title">ไม่มีไฟล์แนบ</h5>
        <button
          v-if="!disabledEdit"
          type="button"
          class="btn btn-light mt-2"
          @click.stop="setActiveModal('SelectSourceModal')">
          เพิ่มไฟล์แนบ
        </button>
      </EmptyMessage>
    </Section>
  </div>
</template>

<script>
// Systems
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import List from '@/components/List.vue';
import Section from '@/components/Section.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import DoubleTabToConfirmButton from '@/components/DoubleTapToConfirmButton.vue';
import FormGroup from '@/components/FormGroup.vue';

// Utils
import dateFormatter from '@/utils/dateFormatter';
import unitFormatter from '@/utils/unitFormatter';
import downloadNameInjector from '@/utils/downloadNameInjector';

// Scoped
import ManageStatusAttachmentItemModal from './ManageStatusAttachmentItemModal.vue';
import SelectSourceModal from './SelectSourceModal.vue';
import FileUploaderModal from './FileUploaderModal.vue';
import ChooseExistingFilesModal from './ChooseExistingFilesModal.vue';
import attachmentMixin from './attachmentMixin';

export default {
  mixins: [attachmentMixin, dateFormatter, unitFormatter, downloadNameInjector],
  components: {
    List,
    Section,
    EmptyMessage,
    DoubleTabToConfirmButton,
    FormGroup,
    ManageStatusAttachmentItemModal,
    SelectSourceModal,
    FileUploaderModal,
    ChooseExistingFilesModal,
  },
  props: {
    disabledEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedAttachmentItemIndex: null,
      editingFile: false,
      editingName: null,
    };
  },
  computed: {
    ...mapGetters([
      'attachment',
      'attachmentID',
      'activeModal',
      'statusOptions',
      'titleIsEmpty',
      'currentVideo',
      'currentProgram',
      'currentEpisode',
    ]),
  },
  async created() {
    await this.initAttachments();
  },
  methods: {
    ...mapActions([
      'setActiveModal',
      'setAttachmentID',
      'inheritAttachment',
      'setTitleError',
      'removeTitleError',
      'loadAttachment',
      'updateFileName',
      'resetErrors',
      'resetAttachment',
    ]),
    async initAttachments() {
      this.$store.commit('resetAttachmentState');
      const attachmentId = this.getContentAttachmentId();
      if (attachmentId) {
        await this.loadAttachment(attachmentId);
      } else {
        this.resetAttachment();
      }
    },
    getContentAttachmentId() {
      if (!_.isEmpty(this.currentVideo)) {
        return this.currentVideo.attachmentId;
      }
      if (!_.isEmpty(this.currentEpisode)) {
        return this.currentEpisode.attachmentId;
      }
      if (!_.isEmpty(this.currentProgram)) {
        return this.currentProgram.attachmentId;
      }
      return null;
    },
    async onSaveFileName() {
      const payload = {
        id: this.editingFile.id,
        name: this.editingName,
      };
      await this.updateFileName(payload);
      await this.initAttachments();
      this.onCancelEditingFileName();
    },
    onFileNameClicked(file) {
      if (this.disabledEdit) return;
      this.editingFile = file;
      this.editingName = file.name;
    },
    onCancelEditingFileName() {
      this.editingFile = false;
      this.editingName = null;
    },
    showModal(modalName) {
      return this.activeModal === modalName;
    },
    updatePublishStatus(itemIndex) {
      const item = this.setItemStatus(this.attachment.files[itemIndex], itemIndex);

      const newFiles = _.map(this.attachment.files, (file, fileIndex) => {
        if (fileIndex === itemIndex) {
          return item;
        }
        return file;
      });

      this.inheritAttachment({ ...this.attachment, files: newFiles });
    },
    changePublishStatus(itemIndex) {
      const item = this.setItemStatus(this.attachment.files[itemIndex], itemIndex);

      if (item.status === 'scheduled') {
        this.setActiveModal('ManageStatusAttachmentItemModal');
        this.onSelectAttachmentItem(itemIndex);
      } else {
        this.updatePublishStatus(itemIndex);
      }
    },
    setItemStatus(item, itemStatus) {
      if (itemStatus === 'publishOnAirAt') {
        return {
          ...item,
          isPublishOnFirstAirAt: true,
        };
      }
      return {
        ...item,
        isPublishOnFirstAirAt: false,
      };
    },
    isEmpty() {
      if (this.attachment) {
        return _.isEmpty(this.attachment.files);
      }
      return true;
    },
    checkTitleRequired() {
      if (!this.attachment.name.length) {
        this.setTitleError();
        return;
      }
      this.removeTitleError();
      this.inheritAttachment({ ...this.attachment });
    },
    onUpdatedScheduleAttachmentItem(attachmentItem) {
      const newFiles = _.map(this.attachment.files, (file, fileIndex) => {
        if (fileIndex === this.selectedAttachmentItemIndex) {
          return attachmentItem;
        }
        return file;
      });
      this.inheritAttachment({ ...this.attachment, files: newFiles });
      this.setActiveModal(null);
    },
    onSelectAttachmentItem(itemIndex) {
      this.selectedAttachmentItemIndex = itemIndex;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/section-container.scss';

.section-attachment-container {
  padding: $spacer * 0.5 $spacer $spacer;
  .list-item-container {
    height: 66px;
    cursor: default;
    .list-item-detail {
      .list-item {
        justify-content: flex-start;
        align-items: flex-start;
      }
    }
    .dropdown {
      width: 100%;
      select {
        width: 100%;
        background: $secondary;
        color: #FFF;
        border-radius: 0.25rem;
        padding: 8px 5px 7px 5px;
        margin: 2px 0 0 0;
        border: none;
      }
    }
    .attachment-scheduler {
      display: flex;
      margin-top: 16px;
      flex-flow: column;
      p {
        margin: 0;
      }
    }
  }
}

.hidden-input {
  display: none;
}

.title {
  cursor: pointer;
}

.input-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  .input-title {
    border: 1px solid $gray-400;
    border-radius: 2px;
    flex: 1;
  }
  .fas {
    cursor: pointer;
    padding: 0 4px;
    margin: 0 4px;
    transition: color 200ms ease;
    &.fa-save {
      &:hover {
        color: $green;
      }
    }
    &.fa-times {
      &:hover {
        color: $red;
      }
    }
  }
}

.alert-danger {
  p {
    margin: 10px 0 6px;
  }
}

.list-item-icon {
  i {
    font-size: 34px;
  }
}
.scheduler-modal {
  padding: 20px;
  .scheduler-modal-footer {
    text-align: right;
  }
}
.a-schedule-attachment {
  cursor: pointer;
  &:hover {
    color: $altv-navy;
  }
}
.attachment-title-container {
  padding: 20px 0 0;
  border-bottom: 1px solid $gray-200;
  margin-bottom: 20px;
}

@media screen and (max-width: 575.98px) {
  .section-attachment-container {
    .list-item-container {
      height: auto;
      .dropdown {
        select {
          padding: 5px;
        }
      }
    }
  }
  .attachment-title-container {
    input {
      width: 80%;
    }
    .btn-remove-attachment {
      margin-top: 0;
      position: absolute;
      top: -60px;
      right: 0;
    }
  }
  .title {
    margin-bottom: 6px;
  }
  .input-container {
    justify-content: flex-start;
    margin-bottom: 4px;
    .input-title {
      flex: auto;
      font-size: 13px;
    }
  }
  .list-item-container {
    .list-item-icon {
      display: none;
    }
    .list-item-detail {
      .list-item {
        padding: 0 !important;
        &.list-item-secondary {
          order: unset;
        }
        &.list-item-action {
          .btn-action {
            display: flex;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 380px) {
  .input-container {
    width: 80%;
  }
}

@media screen and (max-width: 360px) {
  .input-container {
    width: 80%;
  }
  .list-item-container {
    .list-item-detail {
      .list-item {
        &.list-item-action {
          .btn-action {
            display: none;
          }
        }
      }
    }
  }
}
</style>
